import { lazy } from 'react';
import UISuspense from 'core/presentation/ui-suspense';

/**
 * UILazy is a High Order Component (HoC) function used to wrap the component implementation HoC
 * with Suspense functionality and to execute factory function to fetch component modules dynamically with help of preact lazy.
 *
 * @function UILazy
 * @memberof Mojito.Core.Presentation
 *
 * @param {string} name - The name of the view.
 * @param {Function} factory - Factory function to fetch component module. Usually should be implemented with dyncamic import() inside.
 * @param {Function} hoc - HoC implementation, usually one of UIView, UIControllerView or UIPage.
 * @param {object} propTypes - Prop types object.
 * @param {object} defaultProps - Default props object.
 * @param {Function} [loadingView] - Loading view component will be shown while component is suspending.
 *
 * @returns {Function} MojitoComponent function.
 */
export default function UILazy(name, factory, hoc, propTypes, defaultProps, loadingView) {
    const LazyComponent = lazy(async () => {
        const moduleInfo = await factory();
        // React expects that lazy factory function will return the result of dynamic import().
        // Hence we return promise that resolves to the HoC component presented in a format of ES6 module ({default: module}).
        return new Promise(resolve => {
            const hocModule = hoc(
                name,
                moduleInfo?.componentModule?.default,
                propTypes,
                defaultProps
            );
            resolve({ default: hocModule });
        });
    });

    return UISuspense(name, LazyComponent, loadingView);
}
